import React from "react";
import styled, { css, useTheme } from "styled-components";
import { variant } from "styled-system";
import { Icon } from "./Icon";
import { Box } from "./Box";
import { Text } from "./Text";
import { Flex } from "./Flex";
const getVariants = ({ borders, radii, fontSizes, space, colors: { badgeColors } }) => ({
    alert: {
        backgroundColor: badgeColors.alertBackground,
        border: borders[0],
        borderColor: "none",
        color: badgeColors.alertText,
    },
    chip: {
        alignItems: "center",
        backgroundColor: badgeColors.chipBackground,
        border: borders[1],
        borderColor: badgeColors.chipBorder,
        borderRadius: radii[1],
        color: badgeColors.chipText,
        fontSize: fontSizes[1],
        margin: space[1],
        paddingBottom: space[0],
        paddingLeft: space[1],
        paddingRight: space[1],
        paddingTop: space[0],
    },
    critical: {
        backgroundColor: badgeColors.criticalBackground,
        border: borders[0],
        borderColor: "none",
        color: badgeColors.criticalText,
    },
    custom: {
        backgroundColor: badgeColors.customBackground,
        border: borders[0],
        borderColor: "none",
        color: badgeColors.customText,
    },
    debug: {
        backgroundColor: badgeColors.debugBackground,
        border: borders[0],
        borderColor: "none",
        color: badgeColors.debugText,
    },
    default: {
        backgroundColor: badgeColors.defaultBackground,
        border: borders[0],
        borderColor: "none",
        color: badgeColors.defaultText,
    },
    dry: {
        backgroundColor: badgeColors.dryBackground,
        border: borders[0],
        borderColor: "none",
        color: badgeColors.dryText,
    },
    emergency: {
        backgroundColor: badgeColors.emergencyBackground,
        border: borders[0],
        borderColor: "none",
        color: badgeColors.emergencyText,
    },
    error: {
        backgroundColor: badgeColors.errorBackground,
        border: borders[0],
        borderColor: "none",
        color: badgeColors.errorText,
    },
    informational: {
        backgroundColor: badgeColors.informationalBackground,
        border: borders[0],
        borderColor: "none",
        color: badgeColors.informationalText,
    },
    notice: {
        backgroundColor: badgeColors.noticeBackground,
        border: borders[0],
        borderColor: "none",
        color: badgeColors.noticeText,
    },
    primary: {
        backgroundColor: badgeColors.primaryBackground,
        border: borders[0],
        borderColor: "none",
        color: badgeColors.primaryText,
    },
    primaryDark: {
        backgroundColor: badgeColors.primaryDarkBackground,
        border: borders[0],
        borderColor: "none",
        color: badgeColors.primaryDarkText,
    },
    secondary: {
        backgroundColor: badgeColors.secondaryBackground,
        border: borders[0],
        borderColor: "none",
        color: badgeColors.secondaryText,
    },
    success: {
        backgroundColor: badgeColors.successBackground,
        border: borders[0],
        borderColor: "none",
        color: badgeColors.successText,
    },
    warning: {
        backgroundColor: badgeColors.warningBackground,
        border: borders[0],
        borderColor: "none",
        color: badgeColors.warningText,
    },
});
const sizes = {
    condensed: {
        borderRadius: "4px", // missing border radius in theme
        fontSize: "10px",
        // missing padding in theme
        height: "16px",
        padding: "1px 6px",
    },
    normal: {
        // missing size in theme
        borderRadius: 1,
        height: "20px",
        padding: "2px 8px", // missing padding in theme
    },
};
const DEFAULT_VARIANT = "primary";
const StyledBadge = styled(Flex) `
  display: inline-flex;
  font-size: 11px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: center;
  margin: 0 4px;
  white-space: nowrap;
  ${variant({ prop: "size", variants: sizes })}
  ${({ theme, variant = DEFAULT_VARIANT }) => (Object.assign({}, getVariants(theme)[variant]))}
  ${({ onClick }) => onClick &&
    css `
      &:hover {
        cursor: pointer;
      }
    `}
    ${({ disabled }) => disabled &&
    css `
      cursor: not-allowed;
      opacity: 0.7;
      pointer-events: none;
    `}
`;
const CloseButton = ({ children, onClick }) => {
    const { colors: { badgeColors }, } = useTheme();
    return (React.createElement(Box, { color: badgeColors.closeButton, cursor: "pointer", "data-testid": "closeBadge", onClick: onClick, position: "relative", pr: 1, top: "1px" }, children));
};
export const Badge = ({ size = "normal", variant = DEFAULT_VARIANT, onCloseClick, label = "", text = "", onClick, disabled, addTestId = true, children, props, }) => {
    const { colors: { badgeColors }, } = useTheme();
    return (React.createElement(StyledBadge, Object.assign({ p: 2, variant: variant, size: size, onClick: onClick, disabled: disabled }, props, { "data-testid": addTestId ? "badgeTestId" : "" }),
        onCloseClick ? (React.createElement(CloseButton, { onClick: onCloseClick },
            React.createElement(Icon, { src: "Close", size: "12px" }))) : null,
        label && (React.createElement(Text, { color: badgeColors.badgeLabelText, fontWeight: "normal", mr: 1, whiteSpace: "nowrap", "font-size": "11px" },
            label,
            ":",
            " ")),
        text || children));
};
